import { formatSecondsAsTimecode } from '../format-timecode.js'
import {
  MEETING_NOTES_HEADING_LEVELS,
  type MeetingNotesDoc,
  type MeetingNotesNode,
} from './doc-model.js'

/**
 * A meeting note → Markdown, in the dialect `meetingNotesMarkdownToDoc` reads
 * back.
 *
 * Three consumers, all of them user-facing: the MCP tool that hands a note to a
 * model, the panel's 複製, and the panel's 下載 `.md`. None of them is an
 * editor round-trip on its own — but the MCP client may EDIT what it gets and
 * save it back, so an escape missed here is a `- ` that becomes a bullet, or a
 * `2026.` that becomes a numbered list with the year deleted.
 *
 * Like its inverse, this NEVER throws. The schema allows documents Markdown has
 * no spelling for (a table whose first row is not a header row, a cell holding
 * two paragraphs); every one of them has a lossy-but-readable output here,
 * because the alternative is 複製 failing on a note the user is looking at.
 */

/**
 * The characters that would re-parse as inline structure.
 *
 * Deliberately NARROW. The blog's `escapeInlineText` escapes nine more
 * (`~ [ ] < > |` plus three URL-defusing rules) because a blog article is prose
 * a human wrote in an editor; a meeting note is copied into chat windows and
 * documents, where a backslash is visible damage to something the reader never
 * typed. The constructs left unescaped either have no node in this schema and
 * therefore degrade to their own text anyway (`~~`, `<b>`), or keep their words
 * on the way back (`[文字](網址)` → 文字).
 *
 * `_` is the exception that has to be conditional. `marked` reads `_乙_` as
 * emphasis where the hand-written renderer did not, so a note saying「甲 _乙_ 丙」
 * would come back italic with the underscores eaten. CommonMark forbids
 * INTRAWORD `_` emphasis, though, so `snake_case` and `report_2026_final` are
 * already literal — escaping those would put a backslash into the one shape
 * underscores actually appear in. Hence: escape an underscore only where it has
 * a non-word character (or an edge) on at least one side, which is exactly the
 * set `marked` can read as emphasis.
 */
function escapeInlineText(text: string): string {
  return text
    .replace(/([*`])/g, '\\$1')
    .replace(/(?<![\p{L}\p{N}])_|_(?![\p{L}\p{N}])/gu, '\\_')
}

/**
 * Escape a LINE-LEADING marker so a paragraph that merely starts with one is not
 * re-parsed as structure.
 *
 * Every save round-trips through here, and the failure is invisible until
 * reload: a paragraph typed as「2026. 年度計畫」came back as a numbered list item
 * with the `2026.` gone. The marker's PUNCTUATION is escaped, not the whole
 * token — `2026\.` is valid Markdown, `\2026.` is not (digits are not escapable,
 * so the backslash would render literally).
 *
 * The set is wider than the old renderer's by three, because the `marked` lexer
 * claims three shapes the hand-written one never did: `+ ` is a bullet, `# ` and
 * `#### ` are headings (they degrade to paragraphs, which would eat the hashes),
 * and a line of `---`/`===` under a paragraph line is a setext heading.
 */
function escapeLineStart(line: string): string {
  return line
    .replace(/^(\s*)(\d+)([.)])(\s|$)/, '$1$2\\$3$4')
    .replace(/^(\s*)([-*+])(\s|$)/, '$1\\$2$3')
    .replace(/^(#{1,6})(\s|$)/, '\\$1$2')
    .replace(/^(\s*)(>)/, '$1\\$2')
    .replace(/^([-=]{2,}\s*)$/, '\\$1')
}

function nodesOf(node: MeetingNotesNode): MeetingNotesNode[] {
  return Array.isArray(node.content) ? node.content : []
}

function attrsOf(node: MeetingNotesNode): Record<string, unknown> {
  return node.attrs ?? {}
}

/** How a `hardBreak` is written, which depends on where it sits. */
type BreakStyle = 'line' | 'space'

/**
 * One run of inline nodes.
 *
 * `hardBreak` is a CommonMark hard line break (`\` then a newline) everywhere
 * except inside a table cell, where a row IS one line — there it becomes the
 * space it visually is, which is what the old serializer did for the same
 * reason.
 */
function serializeInline(nodes: MeetingNotesNode[], breakStyle: BreakStyle): string {
  let out = ''
  for (const node of nodes) {
    if (node.type === 'hardBreak') {
      out += breakStyle === 'line' ? '\\\n' : ' '
      continue
    }
    if (node.type === 'timecode') {
      const seconds = attrsOf(node).seconds
      out += wrapMarks(
        `\`${formatSecondsAsTimecode(typeof seconds === 'number' ? seconds : 0)}\``,
        node
      )
      continue
    }
    if (node.type === 'text') {
      out += wrapMarks(escapeInlineText(typeof node.text === 'string' ? node.text : ''), node)
      continue
    }
    // An inline node this dialect has no spelling for. Its text still counts.
    out += serializeInline(nodesOf(node), breakStyle)
  }
  return out
}

/** `**` then `*`, innermost-last, matching the nesting the parser reads back. */
function wrapMarks(body: string, node: MeetingNotesNode): string {
  let out = body
  for (const mark of [...(node.marks ?? [])].reverse()) {
    if (mark.type === 'bold') out = `**${out}**`
    else if (mark.type === 'italic') out = `*${out}*`
  }
  return out
}

/** Prefixes every line, leaving a blank line as the bare marker rather than a trailing space. */
function prefixLines(text: string, first: string, rest: string): string {
  const lines = text.split('\n')
  return lines
    .map((line, index) => {
      const prefix = index === 0 ? first : rest
      return line === '' ? prefix.trimEnd() : `${prefix}${line}`
    })
    .join('\n')
}

function serializeParagraph(node: MeetingNotesNode): string {
  return serializeInline(nodesOf(node), 'line').split('\n').map(escapeLineStart).join('\n')
}

function serializeHeading(node: MeetingNotesNode): string {
  const level = attrsOf(node).level
  const depth =
    typeof level === 'number' && (MEETING_NOTES_HEADING_LEVELS as readonly number[]).includes(level)
      ? level
      : 2
  return `${'#'.repeat(depth)} ${serializeInline(nodesOf(node), 'line')}`
}

/**
 * The blocks of one list item.
 *
 * A nested list follows its parent item's text with a single newline — a blank
 * line there turns the whole list loose, which the lexer reports as an extra
 * paragraph wrapper.
 */
function serializeListItem(nodes: MeetingNotesNode[]): string {
  const parts: string[] = []
  for (const [index, node] of nodes.entries()) {
    const nested = node.type === 'bulletList' || node.type === 'orderedList'
    const separator = index === 0 ? '' : nested ? '\n' : '\n\n'
    parts.push(`${separator}${serializeBlock(node)}`)
  }
  return parts.join('')
}

/**
 * A list. The continuation indent is the MARKER's own width, not a fixed two
 * spaces: `1. ` puts its content in column 3, and a child indented only two
 * spaces is a sibling of the parent list, not a child of the item.
 */
function serializeList(node: MeetingNotesNode, ordered: boolean): string {
  const items: string[] = []
  for (const [index, item] of nodesOf(node).entries()) {
    const marker = ordered ? `${index + 1}. ` : '- '
    items.push(prefixLines(serializeListItem(nodesOf(item)), marker, ' '.repeat(marker.length)))
  }
  return items.join('\n')
}

/**
 * One cell: a single line, with `|` defused so a cell's own text cannot split
 * the row into more columns than the table has.
 */
function serializeTableCell(node: MeetingNotesNode): string {
  return nodesOf(node)
    .map((paragraph) => serializeInline(nodesOf(paragraph), 'space'))
    .join(' ')
    .replace(/\|/g, '\\|')
    .trim()
}

/**
 * A table.
 *
 * The divider row is written whatever the first row holds. The schema allows a
 * `tableHeader` anywhere (a paste can put one in the middle) and a first row of
 * plain cells, but GFM has exactly one table shape — header row, divider, body
 * — so a table whose first row is not a header row is written with that row in
 * the header's place. It reads back as a table with a header, which is a change
 * the user can see and fix; refusing to serialize would instead break 複製 on a
 * note that renders perfectly well.
 */
function serializeTable(node: MeetingNotesNode): string {
  const rows = nodesOf(node)
  if (rows.length === 0) return ''
  const columns = Math.max(...rows.map((row) => nodesOf(row).length), 1)
  const line = (row: MeetingNotesNode): string => {
    const cells = nodesOf(row).map(serializeTableCell)
    while (cells.length < columns) cells.push('')
    return `| ${cells.join(' | ')} |`
  }
  return [line(rows[0]), `|${' --- |'.repeat(columns)}`, ...rows.slice(1).map(line)].join('\n')
}

function serializeBlock(node: MeetingNotesNode): string {
  switch (node.type) {
    case 'heading':
      return serializeHeading(node)
    case 'bulletList':
      return serializeList(node, false)
    case 'orderedList':
      return serializeList(node, true)
    case 'blockquote':
      return prefixLines(serializeBlocks(nodesOf(node)), '> ', '> ')
    case 'table':
      return serializeTable(node)
    case 'paragraph':
      return serializeParagraph(node)
    default:
      // A node this dialect has no spelling for. Its blocks still count — the
      // schema can only grow by someone adding a node, and a silent hole in
      // 複製 is the worst way to find out.
      return serializeBlocks(nodesOf(node))
  }
}

function serializeBlocks(nodes: MeetingNotesNode[]): string {
  return nodes
    .map(serializeBlock)
    .filter((block) => block !== '')
    .join('\n\n')
}

/**
 * The trailing newline is part of the contract: the MCP tool's pinned output is
 * `'## 摘要\n\n內容\n'`, and a file downloaded as `.md` should end with one.
 */
export function meetingNotesDocToMarkdown(doc: MeetingNotesDoc): string {
  return `${serializeBlocks(Array.isArray(doc?.content) ? doc.content : [])}\n`
}
